import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { theme, archerConstants, IconButton } from '@archer-technologies/ui-components';
import { DataTypeProvider, FilteringState, SortingState, GroupingState, TreeDataState, IntegratedFiltering, IntegratedSorting, SelectionState, IntegratedGrouping, PagingState, IntegratedPaging, CustomTreeData, CustomPaging } from '@devexpress/dx-react-grid';
import { Grid, DragDropProvider, VirtualTable, Table, TableColumnResizing, TableHeaderRow, TableSelection, TableColumnVisibility, TableGroupRow, Toolbar, GroupingPanel, ColumnChooser, TableTreeColumn, PagingPanel } from '@devexpress/dx-react-grid-material-ui';
import makeStyles from '@mui/styles/makeStyles';
import { useState, useMemo, createElement } from 'react';
import { ActionTypeProvider } from './Columns/Actions.js';
import { CheckTypeProvider } from './Columns/Check.js';
import { DateTypeProvider } from './Columns/Date.js';
import { NumberTypeProvider } from './Columns/Number.js';
import { StatusTypeProvider } from './Columns/Status.js';
import { ActionsPlugin } from './plugins/actions.plugin.js';
import { ColumnChooserItem, ColumnChooserOverlay, ToggleButtonComponent } from './plugins/columnChooser.plugin.js';
import { useManageFilterState } from './plugins/filtering/helpers.js';
import { dateColumnIntegratedFilterPredicate } from './plugins/filtering/DateColumnFilter.js';
import { textColumnIntegratedFilterPredicate, statusColumnFilterPredicate } from './plugins/filtering/TextColumnFilter.js';
import { TableHeaderRowFilterPlugin } from './plugins/filtering/tableHeaderRowFilter.plugin.js';
import { ToolbarClearFilters } from './plugins/filtering/toolbarClearFilters.plugin.js';
import { GroupingPanelItemComponent, GroupingPanelEmptyMessageComponent } from './plugins/groupingPanel.plugin.js';
import { ImagePlugin } from './plugins/image.plugin.js';
import { PagingPanelContainer } from './plugins/pagingPanelContainer.plugin.js';
import { TableComponent, TableCellComponent, TableHeaderRowCellComponent, TableHeaderRowTitleComponent, TableHeaderRowSortLabel, TableHeaderRowGroupButtonComponent, TableRowSelectCellComponent } from './plugins/tableComponent.plugin.js';
import { treeExpandIcon } from './plugins/tableExpandRowComponent.plugin.js';
import { TableGroupRowCellComponent } from './plugins/tableGroupRowComponent.plugin.js';
import { ToolbarAddNew } from './plugins/toolbarAddNew.plugin.js';
import { ToolbarDelete } from './plugins/toolbarDelete.plugin.js';
import { ToolbarEmail } from './plugins/toolbarEmail.plugin.js';
import { ToolbarMore } from './plugins/toolbarMore.plugin.js';
import { GridStateStoringPlugin } from './plugins/gridStateStoring.plugin.js';
import { IntegratedSelectionWrapper } from './plugins/integratedSelection.plugin.js';
import { gridTranslations } from './Translations/translations.js';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

var useStyles = makeStyles(function () {
  // The SortLabel root and active selector is applied twice because an internal MUI selector is overwriting our styles by doing the same thing.
  var sortLabelRoot = '[class*="MuiTableSortLabel-root"]';
  var sortLabelActive = '[class*="MuiTableSortLabel-active"]';
  var svgIcon = '[class*="MuiSvgIcon-root"]';
  return {
    gridRoot: {
      '& [class*="MuiToolbar-root"]': {
        borderBottom: 'none',
        padding: '0px',
        '& .grouping-panel-empty-message': _objectSpread(_objectSpread({}, theme.typography.h5), {}, {
          color: theme.palette.grey[900],
          padding: '0.625rem 0.75rem',
          borderWidth: '1px',
          borderStyle: 'dashed',
          borderColor: theme.palette.grey[600],
          borderRadius: '2px',
          width: '100%'
        }),
        // Pagination Container -- Sibling selector because it gets built with no static class names
        '& + div + div': {
          height: theme.spacing(3),
          marginTop: theme.spacing(3),
          marginBottom: theme.spacing(2),
          overflow: 'visible',
          padding: 0,
          // Rows per page container -- descendant selector because it gets built with no static class names
          '& > div:nth-child(1)': {
            paddingRight: theme.spacing(4),
            // Page size message label
            '& [class*="ABO-PageSizeSelector-label"]': {
              color: theme.typography.body2.color,
              fontSize: theme.typography.body2.fontSize,
              paddingRight: theme.spacing(1)
            },
            // Page Size selector dropdown container
            '& .MuiInput-root': {
              backgroundColor: 'rgba(33, 33, 33, 0.06)',
              color: theme.typography.body2.color,
              fontSize: theme.typography.body2.fontSize,
              minWidth: theme.spacing(7),
              borderTopLeftRadius: theme.shape.borderRadius,
              borderTopRightRadius: theme.shape.borderRadius,
              '& >div': {
                borderBottom: '1px solid rgba(33,33,33,0.5)',
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
              },
              '& >div:hover': {
                borderBottom: '1px solid rgba(33,33,33,1)'
              },
              '& >div[aria-expanded="true"]': {
                borderBottom: '3px solid #176DC2'
              },
              textAlign: 'left',
              // Page Size selector value display
              '& .MuiSelect-root': {
                height: theme.spacing(3)
              },
              '& .MuiInputBase-input': {
                paddingRight: 0,
                paddingTop: 0,
                paddingBottom: 0
              },
              // Page Size Selector Icon
              '& svg': {
                fontSize: '1.5rem',
                top: 'calc(50% - 12px)',
                right: 0,
                color: theme.palette.primary.main
              }
            }
          },
          // Pagination Controls Display Container -- descendant selector because it gets built with no static class names
          '& > div:nth-child(2)': {
            paddingRight: theme.spacing(4),
            // Pagination Rows Label
            '& > span': {
              color: theme.typography.body2.color,
              fontSize: theme.typography.body2.fontSize,
              paddingRight: theme.spacing(4)
            },
            // Pagination Buttons and page number indicators
            '& button': {
              padding: theme.spacing(0, 1),
              margin: 0,
              height: theme.spacing(3),
              minHeight: theme.spacing(3),
              maxHeight: theme.spacing(3)
            },
            '& > button[tabIndex="-1"]:not(:nth-child(2)):not(:last-child)': {
              backgroundColor: theme.palette.grey[100]
            },
            '& button:not(.Mui-disabled)': {
              color: theme.palette.primary.main
            }
          }
        }
      },
      '& [class*="MuiTableRow-root"]': {
        height: '36px'
      },
      '& [class*="MuiTableHead-root"]': {
        '& [class*="MuiTableRow-head"]': {
          '& [class*="MuiTableCell"]:first-child': {
            '& button[class*="IconButton"]': {
              opacity: 0,
              padding: 0,
              '&.infoPopperFilter': {
                opacity: 1,
                padding: theme.spacing(0.5)
              }
            }
          }
        },
        '& [class*="MuiTableCell-root"]': {
          borderBottom: "2px solid ".concat(theme.palette.grey[800]),
          paddingTop: '0px',
          paddingBottom: '0px',
          paddingRight: theme.spacing(0.75),
          paddingLeft: theme.spacing(0.75),
          '&[class*=MuiTableCell-head]': {
            '&:first-child': {
              paddingLeft: theme.spacing(0.75)
            }
          },
          '& [class*="MuiTypography-root"]': {
            textTransform: 'uppercase'
          },
          '& .table-header-row-sort-label': _defineProperty({}, "& ".concat(sortLabelRoot, ", ").concat(sortLabelRoot).concat(sortLabelActive).concat(sortLabelRoot).concat(sortLabelActive), {
            width: '100%',
            '& [class*="MuiTableSortLabel-icon"]': {
              fontSize: theme.spacing(2),
              color: theme.palette.grey[900]
            }
          }),
          '& .table-header-row-group-button': _defineProperty({
            display: 'flex',
            alignItems: 'center'
          }, "& ".concat(svgIcon), {
            fontSize: theme.typography.fontSize,
            color: theme.palette.grey[600]
          }),
          '& [class*="ABO-ResizingControl-resizeHandleFirstLine"]:hover': {
            backgroundColor: 'unset'
          },
          '& [class*="ABO-ResizingControl-resizeHandleSecondLine"]:hover': {
            backgroundColor: 'unset'
          },
          '& [class*="resizeHandleLine"]': {
            backgroundColor: 'unset'
          },
          '& [class*="ResizingControl-resizeHandle"]': {
            right: '14px',
            width: '1px'
          },
          '&:hover': {
            '& .table-header-row-sort-label': _defineProperty({}, "& ".concat(sortLabelRoot, ", ").concat(sortLabelRoot).concat(sortLabelActive).concat(sortLabelRoot).concat(sortLabelActive), {
              '& [class*="MuiTableSortLabel-icon"]': {
                color: theme.palette.primary.main
              }
            }),
            '& .table-header-row-group-button': _defineProperty({}, "& ".concat(svgIcon), {
              color: theme.palette.primary.main
            }),
            '& .table-header-row-group-button.table-header-row-group-button-disabled': _defineProperty({}, "& ".concat(svgIcon), {
              color: theme.palette.grey[600]
            })
          },
          '& .grid-table-header-row-cell-splitter': {
            borderRight: "1px solid ".concat(theme.palette.grey[600]),
            paddingRight: '0.75rem',
            height: '1rem'
          },
          '&:last-child': {
            paddingRight: '0.375rem',
            '& .grid-table-header-row-cell-splitter': {
              display: 'none'
            }
          }
        }
      },
      '& [class*="ABO-MuiTableCell-body"]': {
        verticalAlign: 'top',
        '& [class*="ABO-TableTreeCellBase"]': {
          padding: '0 6px 6px 6px',
          verticalAlign: 'top',
          height: '26px'
        }
      },
      // TableContainer-root
      '& > div:nth-child(2)': {
        overflow: 'visible'
      },
      '& [class*="MuiInputBase-input"]': {
        // paddingLeft: theme.spacing(1),
        minHeight: '1.1875em',
        fontWeight: theme.typography.fontWeightMedium,
        lineHeight: theme.typography.body1.lineHeight,
        fontSize: theme.typography.body1.fontSize,
        color: theme.palette.text.primary
      },
      '& [class*="MuiInputBase-focused"]': {
        backgroundColor: 'rgba(0, 0, 0, 0.09)'
      },
      '& [class*="TableNoDataCell-textContainer"]': {
        // ie11 only - position: relative is overridden in Platform, so it is removed.
        '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
          marginLeft: '50%'
        }
      },
      '& [class*="tdCellStyles"] [class*="MuiCheckbox-root"]': {
        padding: '7px 0'
      },
      '& [class*="MuiTableBody-root"]': {
        '& > tr >td': {
          borderBottom: "1px solid ".concat(theme.palette.grey[600])
        },
        '& tr:nth-child(even):not(.TableSelectRow-selected)': {
          backgroundColor: '#F7F7F7'
        },
        '& [class*="MuiTableCell-root"]': {
          backgroundColor: 'inherit',
          padding: '7px 0 7px 5px',
          '& [class*="MuiIconButton-root"]': {
            margin: 0
          },
          '& [class*="MuiTableCell-cell"]': {
            padding: 0
          }
        },
        '& [class*="MuiTableCell-root"]:first-child': {
          paddingBottom: '0'
        },
        '& .table-group-row-cell div': {
          backgroundColor: 'inherit'
        }
      },
      '& [class*="TableSelectRow-selected"]': {
        backgroundColor: archerConstants.archerHover
      },
      '& td> div> button> span': {
        '& svg[class="ABO-MuiSvgIcon-root"]': {
          color: theme.palette.grey[900]
        }
      },
      '& [class*="MuiTypography-root"]': {
        textAlign: 'left'
      }
    },
    groupIconColors: {
      color: theme.palette.grey[900]
    }
  };
});

var Root = function Root(props) {
  var _useStyles = useStyles(),
      gridRoot = _useStyles.gridRoot;

  return /*#__PURE__*/createElement(Grid.Root, _extends({}, props, {
    className: gridRoot,
    style: {
      height: '100%',
      width: '100%'
    }
  }));
}; // const label = { inputProps: { 'aria-label': 'SelectAll' } };


var ArcherGrid = function ArcherGrid(props) {
  var columns = props.columns,
      images = props.images,
      imageValue = props.imageValue,
      actions = props.actions,
      actionMessages = props.actionMessages,
      numberOptions = props.numberOptions,
      expandedGroups = props.expandedGroups,
      filters = props.filters,
      filtersDefault = props.filtersDefault,
      grouping = props.grouping,
      groupingColumnExtensions = props.groupingColumnExtensions,
      isAddNewEnabled = props.isAddNewEnabled,
      isEmailEnabled = props.isEmailEnabled,
      isDeleteEnabled = props.isDeleteEnabled,
      isFilteringEnabled = props.isFilteringEnabled,
      isGroupingEnabled = props.isGroupingEnabled,
      isSortingEnabled = props.isSortingEnabled,
      local = props.local,
      onAddNew = props.onAddNew,
      onEmailClick = props.onEmailClick,
      onDeleteClick = props.onDeleteClick,
      onExpandedGroupsChange = props.onExpandedGroupsChange,
      onFiltersChange = props.onFiltersChange,
      showGroupingPanel = props.showGroupingPanel,
      onGroupingChange = props.onGroupingChange,
      rows = props.rows,
      defaultSorting = props.defaultSorting,
      sorting = props.sorting,
      onSortingChange = props.onSortingChange,
      showAddNew = props.showAddNew,
      showColumnChooser = props.showColumnChooser,
      showEmail = props.showEmail,
      showDelete = props.showDelete,
      pageSizes = props.pageSizes,
      showPagination = props.showPagination,
      totalCount = props.totalCount,
      currentPage = props.currentPage,
      changePageSize = props.changePageSize,
      changeCurrentPage = props.changeCurrentPage,
      pageSize = props.pageSize,
      translate = props.translate,
      dateTimeLocale = props.dateTimeLocale,
      dateTimeLocaleCode = props.dateTimeLocaleCode,
      columnSortingState = props.columnSortingState,
      columnFilteringState = props.columnFilteringState,
      columnSortingFnOverrides = props.columnSortingFnOverrides,
      columnGroupingFnOverrides = props.columnGroupingFnOverrides,
      columnVisibilityState = props.columnVisibilityState,
      defaultColumnVisibilityState = props.defaultColumnVisibilityState,
      columnWidths = props.columnWidths,
      defaultColumnWidths = props.defaultColumnWidths,
      onColumnWidthsChange = props.onColumnWidthsChange,
      columnResizeEnabledNextColumn = props.columnResizeEnabledNextColumn,
      enableRowSelection = props.enableRowSelection,
      enableTreeView = props.enableTreeView,
      nestedTreeColumn = props.nestedTreeColumn,
      onRowSelection = props.onRowSelection,
      enableVirtualTable = props.enableVirtualTable,
      columnExtensions = props.columnExtensions,
      imagePath = props.imagePath,
      rowSelectionType = props.rowSelectionType,
      setExpandedRowIds = props.setExpandedRowIds,
      expandedRowIds = props.expandedRowIds,
      getChildRows = props.getChildRows,
      getRowId = props.getRowId,
      toolbarMenuItems = props.toolbarMenuItems,
      storageKey = props.storageKey;

  var _React$useState = useState([]),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      selection = _React$useState2[0],
      setSelection = _React$useState2[1];

  var changeSingleRowSelection = function changeSingleRowSelection(currentSelections) {
    var lastSelected = null;

    if (selection === [] || selection.length === 0) {
      var _currentSelections = _slicedToArray(currentSelections, 1);

      lastSelected = _currentSelections[0];
    } else {
      lastSelected = currentSelections.find(function (selected) {
        return selection.indexOf(selected) === -1;
      });
    }

    setSelection([lastSelected]);
    onRowSelection(rows[lastSelected]);
  };

  var changeMultipleRowSelection = function changeMultipleRowSelection(currentSelections) {
    var enabledSelection = null;

    if (rows[0].disabledIndex !== undefined) {
      enabledSelection = currentSelections.filter(function (data) {
        var _rows$data;

        return (_rows$data = rows[data]) === null || _rows$data === void 0 ? void 0 : _rows$data.disabledIndex;
      });
    } else {
      enabledSelection = currentSelections;
    }

    setSelection(enabledSelection);
    onRowSelection(enabledSelection);
  };

  var deleteHandler = function deleteHandler(event) {
    if (onDeleteClick) {
      onDeleteClick(event);
      setSelection([]);
    }
  };

  var gridTranslationMessages = useMemo(function () {
    return gridTranslations(translate);
  }, [translate]);

  var _useManageFilterState = useManageFilterState(onFiltersChange),
      addClearFilterStateFn = _useManageFilterState.addClearFilterStateFn,
      removeClearFilterStateFn = _useManageFilterState.removeClearFilterStateFn,
      handleClearFilters = _useManageFilterState.handleClearFilters,
      notifyFiltersChanged = _useManageFilterState.notifyFiltersChanged;

  var integratedFilteringColumnExtensions = columns.map(function (column) {
    return {
      columnName: column.name,
      predicate: getColumnFilterPredicate(column)
    };
  });
  var actionColumnNames = columns.filter(function (column) {
    return column.type === 'actions';
  });
  var columnFilteringStateWithoutActions = columnFilteringState === null || columnFilteringState === void 0 ? void 0 : columnFilteringState.concat(actionColumnNames.map(function (column) {
    return {
      columnName: column.name,
      filteringEnabled: false
    };
  }));
  var columnSortingStateWithoutActions = columnSortingState === null || columnSortingState === void 0 ? void 0 : columnSortingState.concat(actionColumnNames.map(function (column) {
    return {
      columnName: column.name,
      sortingEnabled: false
    };
  }));

  var groupIcon = function groupIcon(_ref) {
    var expanded = _ref.expanded;

    var _useStyles2 = useStyles(),
        groupIconColors = _useStyles2.groupIconColors;

    return /*#__PURE__*/createElement(IconButton, {
      icon: expanded ? 'ArrowDropDown' : 'ArrowRight',
      className: groupIconColors
    });
  };

  return /*#__PURE__*/createElement(Grid, {
    rootComponent: Root,
    rows: rows,
    columns: columns,
    getRowId: getRowId
  }, /*#__PURE__*/createElement(DateTypeProvider, {
    "for": columns.filter(function (column) {
      return column.type === 'date';
    }).map(function (column) {
      return column.name;
    }),
    dateTimeLocale: dateTimeLocale
  }), /*#__PURE__*/createElement(CheckTypeProvider, {
    "for": columns.filter(function (column) {
      return column.type === 'check';
    }).map(function (column) {
      return column.name;
    })
  }), /*#__PURE__*/createElement(NumberTypeProvider, {
    "for": columns.filter(function (column) {
      return column.type === 'number';
    }).map(function (column) {
      return column.name;
    }),
    availableFilterOperations: numberOptions ? numberOptions.availableFilterOperations : ['equal', 'notEqual', 'greaterThan', 'greaterThanOrEqual', 'lessThan', 'lessThanOrEqual']
  }), /*#__PURE__*/createElement(ActionTypeProvider, {
    "for": columns.filter(function (column) {
      return column.type === 'actions';
    }).map(function (column) {
      return column.name;
    }),
    availableFilterOperations: []
  }), /*#__PURE__*/createElement(StatusTypeProvider, {
    "for": columns.filter(function (column) {
      return column.type === 'status';
    }).map(function (column) {
      return column.name;
    }),
    availableFilterOperations: ['contains', 'startsWith', 'endsWith', 'equal', 'notEqual', 'empty', 'notEmpty']
  }), /*#__PURE__*/createElement(DataTypeProvider, {
    "for": columns.filter(function (column) {
      return column.type === undefined;
    }).map(function (column) {
      return column.name;
    }),
    availableFilterOperations: ['contains', 'startsWith', 'endsWith', 'equal', 'notEqual', 'empty', 'notEmpty']
  }), /*#__PURE__*/createElement(FilteringState, {
    columnFilteringEnabled: isFilteringEnabled,
    filters: filters,
    defaultFilters: filtersDefault,
    onFiltersChange: notifyFiltersChanged,
    columnExtensions: columnFilteringStateWithoutActions
  }), /*#__PURE__*/createElement(SortingState, {
    columnSortingEnabled: isSortingEnabled,
    sorting: sorting,
    defaultSorting: defaultSorting,
    onSortingChange: onSortingChange,
    columnExtensions: columnSortingStateWithoutActions
  }), /*#__PURE__*/createElement(GroupingState, {
    columnGroupingEnabled: isGroupingEnabled,
    columnExtensions: groupingColumnExtensions,
    grouping: grouping,
    onGroupingChange: onGroupingChange,
    expandedGroups: expandedGroups,
    onExpandedGroupsChange: onExpandedGroupsChange
  }), enableTreeView && /*#__PURE__*/createElement(TreeDataState, {
    expandedRowIds: expandedRowIds,
    onExpandedRowIdsChange: setExpandedRowIds
  }), local && /*#__PURE__*/createElement(IntegratedFiltering, {
    columnExtensions: integratedFilteringColumnExtensions
  }), local && /*#__PURE__*/createElement(IntegratedSorting, {
    columnExtensions: columnSortingFnOverrides
  }), enableRowSelection && /*#__PURE__*/createElement(SelectionState, {
    selection: selection,
    onSelectionChange: rowSelectionType === 'single' ? changeSingleRowSelection : changeMultipleRowSelection
  }), enableRowSelection && rowSelectionType === 'multiple' && /*#__PURE__*/createElement(IntegratedSelectionWrapper, null), /*#__PURE__*/createElement(IntegratedGrouping, {
    columnExtensions: columnGroupingFnOverrides
  }), showPagination && /*#__PURE__*/createElement(PagingState, {
    currentPage: currentPage,
    onCurrentPageChange: function onCurrentPageChange(page) {
      setSelection([]);

      if (typeof changeCurrentPage === 'function') {
        changeCurrentPage(page);
      }
    },
    onPageSizeChange: changePageSize,
    pageSize: pageSize
  }), local && showPagination && /*#__PURE__*/createElement(IntegratedPaging, null), /*#__PURE__*/createElement(DragDropProvider, null), enableTreeView && getChildRows && /*#__PURE__*/createElement(CustomTreeData, {
    getChildRows: getChildRows
  }), enableVirtualTable ? /*#__PURE__*/createElement(VirtualTable, {
    height: "auto",
    tableComponent: TableComponent,
    cellComponent: TableCellComponent,
    columnExtensions: columnExtensions
  }) : /*#__PURE__*/createElement(Table, {
    messages: gridTranslationMessages,
    tableComponent: TableComponent,
    cellComponent: TableCellComponent,
    columnExtensions: columnExtensions
  }), columnResizeEnabledNextColumn && /*#__PURE__*/createElement(TableColumnResizing, {
    columnWidths: columnWidths,
    defaultColumnWidths: defaultColumnWidths,
    onColumnWidthsChange: onColumnWidthsChange,
    resizingMode: "nextColumn"
  }), /*#__PURE__*/createElement(TableHeaderRow, {
    showSortingControls: isSortingEnabled,
    cellComponent: TableHeaderRowCellComponent,
    titleComponent: TableHeaderRowTitleComponent,
    sortLabelComponent: TableHeaderRowSortLabel,
    groupButtonComponent: TableHeaderRowGroupButtonComponent,
    messages: gridTranslationMessages
  }), enableRowSelection && /*#__PURE__*/createElement(TableSelection, {
    selectByRowClick: enableRowSelection,
    highlightRow: enableRowSelection,
    showSelectionColumn: true,
    showSelectAll: rowSelectionType === 'multiple',
    cellComponent: TableRowSelectCellComponent // headerCellComponent={headerCellComponentProps => {
    //   return (
    //     <th>
    //       <MatCheckbox {...label} {...headerCellComponentProps} />
    //     </th>
    //   );
    // }}

  }), (showColumnChooser || columnVisibilityState || defaultColumnVisibilityState) && /*#__PURE__*/createElement(TableColumnVisibility, {
    messages: gridTranslations(translate),
    hiddenColumnNames: columnVisibilityState,
    defaultHiddenColumnNames: defaultColumnVisibilityState
  }), isFilteringEnabled && /*#__PURE__*/createElement(TableHeaderRowFilterPlugin, {
    messages: gridTranslationMessages,
    translate: translate,
    dateTimeLocaleCode: dateTimeLocaleCode,
    addClearFilterStateFn: addClearFilterStateFn,
    removeClearFilterStateFn: removeClearFilterStateFn,
    theme: theme
  }), isGroupingEnabled && /*#__PURE__*/createElement(TableGroupRow, {
    iconComponent: groupIcon,
    cellComponent: TableGroupRowCellComponent
  }), /*#__PURE__*/createElement(Toolbar, null), isGroupingEnabled && showGroupingPanel && /*#__PURE__*/createElement(GroupingPanel, {
    messages: gridTranslationMessages,
    itemComponent: GroupingPanelItemComponent,
    showGroupingControls: isGroupingEnabled,
    showSortingControls: isSortingEnabled,
    emptyMessageComponent: GroupingPanelEmptyMessageComponent
  }), isFilteringEnabled && /*#__PURE__*/createElement(ToolbarClearFilters, {
    messages: gridTranslationMessages,
    handleClearFilters: handleClearFilters
  }), showColumnChooser && /*#__PURE__*/createElement(ColumnChooser, {
    messages: gridTranslationMessages,
    itemComponent: ColumnChooserItem,
    overlayComponent: ColumnChooserOverlay,
    toggleButtonComponent: ToggleButtonComponent
  }), enableTreeView && /*#__PURE__*/createElement(TableTreeColumn, {
    "for": nestedTreeColumn,
    expandButtonComponent: treeExpandIcon(gridTranslationMessages)
  }), showEmail && /*#__PURE__*/createElement(ToolbarEmail, {
    translate: translate,
    onClick: onEmailClick,
    disabled: !isEmailEnabled
  }), showAddNew && /*#__PURE__*/createElement(ToolbarAddNew, {
    translate: translate,
    onClick: onAddNew,
    disabled: !isAddNewEnabled
  }), showDelete && /*#__PURE__*/createElement(ToolbarDelete, {
    translate: translate,
    onClick: deleteHandler,
    disabled: !isDeleteEnabled
  }), toolbarMenuItems && toolbarMenuItems.length > 0 && /*#__PURE__*/createElement(ToolbarMore, {
    translate: translate,
    menuItems: toolbarMenuItems
  }), images && /*#__PURE__*/createElement(ImagePlugin, {
    translate: translate,
    imageValue: imageValue,
    imagePath: imagePath
  }), actions && /*#__PURE__*/createElement(ActionsPlugin, {
    translate: translate,
    actions: actions,
    messages: actionMessages
  }), showPagination && /*#__PURE__*/createElement(CustomPaging, {
    totalCount: totalCount
  }), showPagination && /*#__PURE__*/createElement(PagingPanel, {
    messages: gridTranslationMessages,
    pageSizes: pageSizes,
    containerComponent: PagingPanelContainer
  }), storageKey && /*#__PURE__*/createElement(GridStateStoringPlugin, {
    storageKey: storageKey
  }));
};

ArcherGrid.defaultProps = {
  showPagination: false,
  translate: function translate(value) {
    return value;
  }
};
ArcherGrid.displayName = 'ArcherGrid';
/**
 * Utilities
 */

var getColumnFilterPredicate = function getColumnFilterPredicate(column) {
  switch (column.type) {
    case 'check':
    case 'number':
      return IntegratedFiltering.defaultPredicate;

    case 'date':
      return dateColumnIntegratedFilterPredicate;

    case 'status':
      return statusColumnFilterPredicate;

    default:
      return textColumnIntegratedFilterPredicate;
  }
};

export { ArcherGrid };